<template>
  <div>
    <div v-if="validData" id="graph_wrapper" v-resize="resizeGraph">
      <svg id="period_graph" ref="period_graph" />
    </div>
    <v-container v-else>
      <v-row align="center" justify="center" style="height: 206px">
        <span style="color: lightgrey; font-size: 150%">Žádna data</span>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import * as d3 from "d3";
import { DateTime, Interval } from "luxon";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import utils from "@/utils";

export default {
  name: "PeriodGraph",
  props: {
    date: {
      type: String
    }
  },

  data() {
    return {
      validData: true,
      graphs: null
    };
  },

  methods: {
    print() {
      const win = parent.window.open("", "", "status=1,width=1024,height=600");
      win.document.write("<?xml version=\"1.0\" standalone=\"no\"?><!DOCTYPE svg PUBLIC \"-//W3C//DTD SVG 1.1//EN\" \"http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd\">" +
        this.$refs.period_graph.outerHTML);
      win.print();
      win.close();
    },

    exportToPDF() {
      const element = this.createGraphs(this.graphs, false);

      if (element !== null) {
        const width = 1600 + 10;
        const height = 500 + 30;

        const svg = Array.from(element)[0];

        const doc = new jsPDF(
          width > height ? "landscape" : "portrait", "px", [width, height]);
        doc.setProperties({
          title: this.$store.state.history.vehicle.numericPlate + ".pdf"
        }).svg(svg, { width: width, height: height })
          .then(() => {
            doc.output("pdfobjectnewwindow", { filename: this.$store.state.history.vehicle.numericPlate + ".pdf" });
          })
          .finally(() => {
            d3.select(svg).remove();
            // workaeound pro zobrazeni toltipu
            this.resizeGraph();
          });
      }
    },

    fetcher(def, interval) {
      if (this.$store.state.history.locations === null || this.$store.state.history.locations.length === 0)
        return [];
      else if (def.measure) {
        return (interval !== null)
          ? this.$store.state.history.locations
            .filter(x => interval.contains(DateTime.fromISO(x.timestamp)) && x.measure !== null && def.name in x.measure)
            .map(x => ({ ts: DateTime.fromISO(x.timestamp), value: def.getter(x) }))
          : this.$store.state.history.locations
            .filter(x => x.measure !== null && def.name in x.measure)
            .map(x => ({ ts: DateTime.fromISO(x.timestamp), value: def.getter(x) }));
      } else {
        return (interval !== null)
          ? this.$store.state.history.locations
            .filter(x => interval.contains(DateTime.fromISO(x.timestamp)))
            .map(x => ({ ts: DateTime.fromISO(x.timestamp), value: def.getter(x) }))
          : this.$store.state.history.locations
            .map(x => ({ ts: DateTime.fromISO(x.timestamp), value: def.getter(x) }));
      }
    },

    refresh() {
      const graphs = [];

      const interval = (this.$store.state.history.ride !== null)
        ? Interval.fromDateTimes(
          DateTime.fromISO(this.$store.state.history.ride.fromTimestamp),
          DateTime.fromISO(this.$store.state.history.ride.toTimestamp))
        : null;

      this.$store.state.history.selected.forEach(x => {
        if (x in this.$store.state.history.defs) {
          const def = this.$store.state.history.defs[x];

          if (!def.measure || this.$store.state.history.parametersFetched.includes(x)) {
            const data = this.fetcher(def, interval);

            if (data.length > 0)
              graphs.push({ def: def, data: data });
          }
        }
      });

      if (graphs.length === 0) {
        this.validData = false;
      } else if (!this.validData) {
        this.validData = true;
        this.$nextTick().then(() => {
          this.createGraphs(graphs);
        });
      } else
        this.createGraphs(graphs);
    },

    resizeGraph() {
      this.createGraphs(this.graphs);
    },

    createGraphs(graphs, draw = true) {
      if (graphs === undefined || graphs === null)
        return null;
      else if (draw)
        this.graphs = graphs;

      var width;
      var height;
      var marginLeft;
      var marginRight;
      var marginBottom;
      var svg;

      if (draw) {
        const wrapper = document.getElementById("graph_wrapper");
        if (wrapper === null)
          return null;

        width = wrapper.clientWidth - 10;
        height = 180;
        marginLeft = graphs[0].def.margin;
        marginRight = (graphs.length > 1) ? graphs[1].def.margin : 0;
        marginBottom = 20;

        svg = d3.select("svg#period_graph")
          .attr("width", width + 10)
          .attr("height", height + marginBottom);
        svg.selectAll("*").remove();
      } else {
        width = 1600;
        height = 500;
        marginLeft = graphs[0].def.margin;
        marginRight = (graphs.length > 1) ? graphs[1].def.margin : 0;
        marginBottom = 20;

        svg = d3.select("body").append("svg");
      }

      const g = svg.append("g");

      // osa X  grafu
      const x = d3
        .scaleTime()
        .domain(
          d3.extent(graphs[0].data, function(d) {
            return d.ts;
          })
        )
        .rangeRound([marginLeft, width - marginRight]);

      let i = 0;
      graphs.forEach(graph => {
        // osa Y jednotlivych grafu
        const y = d3
          .scaleLinear()
          .domain(
            d3.extent(graph.data, function(d) {
              return d.value;
            })
          )
          .rangeRound([height, 10]);
        graph.y = y;

        //4. Creating a Line
        const line = d3
          .area()
          .x(function(d) {
            return x(d.ts);
          })
          .y(function(d) {
            return y(d.value);
          });

        const area = d3
          .area()
          .x(function(d) {
            return x(d.ts);
          })
          .y0(y(d3.extent(y.domain())[0]))
          .y1(function(d) {
            return y(d.value);
          });

        // definice gadientu
        var lg = g.append("defs").append("linearGradient")
          .attr("id", "grad-" + graph.def.name)
          .attr("x1", "0%")
          .attr("x2", "0%")
          .attr("y1", "0%")
          .attr("y2", "100%");

        lg.append("stop")
          .attr("offset", "30%")
          .style("stop-color", graph.def.color)
          .style("stop-opacity", 1);

        lg.append("stop")
          .attr("offset", "100%")
          .style("stop-color", "white")
          .style("stop-opacity", 1);

        // vlastní graf
        g.append("path")
          .datum(graph.data)
          .attr("fill", "url(#grad-" + graph.def.name + ")")
          .attr("fill-opacity", 0.2)
          .attr("stroke-width", 0)
          .attr("d", area);


        g.append("path")
          .datum(graph.data)
          .attr("fill", "none")
          .attr("stroke", graph.def.color)
          .attr("stroke-width", 2)
          .attr("stroke-bottom", false)
          .attr("d", line);

        // osy - pouze pro první graf
        if (i === 0) {
          g.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x).ticks(width / 80, "%H:%M"))
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").clone()
              .attr("y2", -height + 8)
              .attr("stroke-opacity", 0.1))
            .call(g => g.append("text")
              .attr("x", width - 4)
              .attr("y", -4)
              .attr("font-weight", "bold")
              .attr("text-anchor", "end")
              .attr("fill", "currentColor"));

          g.append("g")
            .attr("transform", `translate(${marginLeft},0)`)
            .call(d3.axisLeft(y).ticks(height / 25).tickFormat(x => graph.def.format(x)))
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").clone()
              .attr("x2", width - marginLeft - marginRight)
              .attr("stroke-opacity", 0.1))
            .call(g => g.select(".tick:last-of-type text").clone()
              .attr("x", 0)
              .attr("y", 8)
              .attr("text-anchor", "end")
              .attr("class", "axle-text-outline")
              .attr("transform", "rotate(270)")
              .attr("font-size", "1.1em")
              .attr("color", graph.def.color)
              .text((draw)
                ? this.$t("map.panel.history.graph." + graph.def.name + ".axle")
                : utils.normalize(this.$t("map.panel.history.graph." + graph.def.name + ".axle"))));
        } else if (i === 1) {
          g.append("g")
            .attr("transform", `translate(${width - marginRight},0)`)
            .call(d3.axisRight(y).ticks(height / 25).tickFormat(x => graph.def.format(x)))
            .call(g => g.select(".domain").remove())
            .call(g => g.select(".tick:last-of-type text").clone()
              .attr("x", 0)
              .attr("y", -10)
              .attr("text-anchor", "end")
              .attr("transform", "rotate(270)")
              .attr("class", "axle-text-outline")
              .attr("font-size", "1.1em")
              .attr("color", graph.def.color)
              .text((draw)
                ? this.$t("map.panel.history.graph." + graph.def.name + ".axle")
                : utils.normalize(this.$t("map.panel.history.graph." + graph.def.name + ".axle"))));
        }

        i += 1;
      });

      if (draw)
        this.createGraphTooltip(svg, x, graphs, width, height, marginLeft, marginRight);

      return svg;
    },

    createGraphTooltip(svg, x, graphs, width, height, marginLeft, marginRight) {
      // tooltip
      const focus = svg
        .append("g")
        .attr("class", "focus")
        .attr("transform", "translate(" + marginLeft + "," + 0 + ")");

      const mouseLine = focus
        .append("path")
        //.attr("class", "mouse-line")
        .attr("stroke", "rgba(164,164,164,0.60)")
        .attr("stroke-width", 2)
        .attr("opacity", "0")
        .attr("transform", "translate(" + -marginLeft + "," + 10 + ")");
      const tooltip = focus.append("g")
        .attr("class", "graph-tooltip-wrapper")
        .attr("display", "none");
      const tooltipBackground = tooltip
        .append("rect")
        .attr("transform", "translate(" + -marginLeft + "," + 0 + ")")
        .attr("rx", "8")
        .attr("stroke", "grey")
        .attr("fill", "white");
      const tooltipText = tooltip
        .append("text")
        .attr("transform", "translate(" + -marginLeft + "," + 0 + ")");

      var rectOverlay = svg
        .append("rect")
        .attr("cursor", "move")
        .attr("fill", "none")
        .attr("pointer-events", "all")
        //.attr("class", "zoom")
        .attr("width", width - marginLeft - marginRight)
        .attr("height", height)
        .attr("transform", "translate(" + marginLeft + "," + 0 + ")")
        .on("mousemove", focusMouseMove)
        .on("mouseover", focusMouseOver)
        .on("mouseout", focusMouseOut);

      const thisCall = this;

      function focusMouseMove(e) {
        tooltip.attr("display", null);
        const mouse = d3.pointer(e);
        const ts_mouse = x.invert(mouse[0] + marginLeft);
        const nearest_index = d3.bisect(graphs[0].data.map(d => d.ts), ts_mouse);
        const d0 = (nearest_index > 0)
          ? graphs[0].data[nearest_index - 1].ts
          : graphs[0].data[nearest_index].ts;
        const d1 = (nearest_index >= graphs[0].data.length)
          ? graphs[0].data[nearest_index - 1].ts
          : graphs[0].data[nearest_index].ts;
        var closest_ts;
        if (d0 < x.domain()[0]) {
          closest_ts = d1;
        } else if (d1 > x.domain()[1]) {
          closest_ts = d0;
        } else {
          closest_ts = ts_mouse - d0 > d1 - ts_mouse ? d1 : d0;
        }

        const nearestDateXCord = x(closest_ts);

        mouseLine
          .attr("d", `M ${nearestDateXCord} 0 V ${height - 10}`)
          .attr("opacity", "1");

        tooltipText.selectAll(".tooltip-text-line").remove();
        focus.selectAll(".tooltip-line-circles").remove();
        tooltipText
          .append("tspan")
          .attr("class", "tooltip-text-line")
          .attr("x", "5")
          .attr("y", "5");

        graphs.forEach(graph => {
          const value = graph.data.find(d => d.ts.equals(closest_ts));

          if (value !== undefined) {
            focus
              .append("circle")
              .attr("class", "tooltip-line-circles")
              .attr("r", 5)
              .attr("fill", graph.def.color)
              .attr("stroke", "white")
              .attr("stroke-width", "2")
              .attr("transform", "translate(" + -marginLeft + "," + 0 + ")")
              .attr("cx", nearestDateXCord)
              .attr("cy", graph.y(value.value));

            tooltipText
              .append("tspan")
              .attr("class", "tooltip-text-line")
              .attr("x", "5")
              .attr("dy", "18px")
              .attr("fill", graph.def.color)
              .text(`⏺ ${graph.def.format(value.value)} ${thisCall.$t("map.panel.history.graph." + graph.def.name + ".unit")}`);
          }
        });

        //nearestDateYValues
        svg.property("value", []).dispatch("input", { bubbles: true });

        var tooltipWidth = tooltipText.node().getBBox().width;
        var tooltipHeight = tooltipText.node().getBBox().height;
        var rectOverlayWidth = rectOverlay.node().getBBox().width;
        tooltipBackground
          .attr("width", tooltipWidth + 10)
          .attr("height", tooltipHeight + 6);
        if (nearestDateXCord + tooltipWidth >= rectOverlayWidth) {
          tooltip.attr("transform", "translate(" + (nearestDateXCord - tooltipWidth - 20) + "," + mouse[1] + ")");
        } else {
          tooltip.attr("transform", "translate(" + (nearestDateXCord + 10) + "," + mouse[1] + ")");
        }

        thisCall.$emit("location", closest_ts);
      }

      function focusMouseOver() {
        mouseLine.attr("opacity", "1");
        tooltip.attr("display", null);

        thisCall.$emit("location", null);
      }

      function focusMouseOut() {
        mouseLine.attr("opacity", "0");
        tooltip.attr("display", "none");
        focus.selectAll(".tooltip-line-circles").remove();
        svg.dispatch("input", { bubbles: true });

        thisCall.$emit("location", null);
      }
    }
  },

  watch: {
    "$store.state.history.selected": function() {
      this.refresh();
    },

    "$store.state.history.locations": function() {
      this.refresh();
    },

    "$store.state.history.ride": function() {
      this.refresh();
    }
  }
  ,

  mounted() {
    this.refresh();
  }
};
</script>

<style>
.axle-text-outline {
  font-size: 120%;
  paint-order: stroke;
  stroke: white;
  stroke-width: 4px;
  stroke-linecap: butt;
  stroke-linejoin: miter;
}

</style>